// Shared pieces for the cash register (Register screen, Take payment's Cash
// and Check). The server works in integer cents; these format and parse.

// The register this device works on, remembered per device (a counter tablet
// stays on "Front counter"). Never required: with one register open the
// server picks it.
const DEVICE_KEY = 'register_id';

export function getDeviceRegisterId() {
  try { return localStorage.getItem(DEVICE_KEY) || null; } catch (_) { return null; }
}

export function setDeviceRegisterId(id) {
  try {
    if (id) localStorage.setItem(DEVICE_KEY, id);
    else localStorage.removeItem(DEVICE_KEY);
  } catch (_) { /* private mode: not remembered */ }
}

// "Print receipt" or "No receipt" after a sale, remembered per device: the
// last choice is the one offered first next time. 'print' until chosen.
const RECEIPT_KEY = 'receipt_choice';

export function getReceiptChoice() {
  try { return localStorage.getItem(RECEIPT_KEY) === 'none' ? 'none' : 'print'; } catch (_) { return 'print'; }
}

export function setReceiptChoice(choice) {
  try { localStorage.setItem(RECEIPT_KEY, choice === 'none' ? 'none' : 'print'); } catch (_) { /* private mode: not remembered */ }
}

export const formatCents = (cents) => {
  const n = Number(cents || 0);
  return `${n < 0 ? '−' : ''}$${(Math.abs(n) / 100).toFixed(2)}`;
};

/** Money taken out of the drawer: "−$12.50", and plain "$0.00" for nothing. */
export const formatOutCents = (cents) => (Number(cents) ? `−${formatCents(cents)}` : formatCents(0));

/** "$12.50" / "12.5" / "12" → 1250; null when it isn't a non-negative amount. */
export function dollarsToCents(text) {
  const v = String(text ?? '').replace(/[$,\s]/g, '');
  if (!v || !/^\d*(\.\d{0,2})?$/.test(v) || v === '.') return null;
  return Math.round(Number(v) * 100);
}

export const centsToInput = (cents) => (cents === null || cents === undefined ? '' : (Number(cents) / 100).toFixed(2));

// Mirrors backend services/cashRegister.js DENOMINATIONS.
export const DENOMINATIONS = [
  { key: 'b100', cents: 10000, label: '$100', group: 'Bills' },
  { key: 'b50', cents: 5000, label: '$50', group: 'Bills' },
  { key: 'b20', cents: 2000, label: '$20', group: 'Bills' },
  { key: 'b10', cents: 1000, label: '$10', group: 'Bills' },
  { key: 'b5', cents: 500, label: '$5', group: 'Bills' },
  { key: 'b1', cents: 100, label: '$1', group: 'Bills' },
  { key: 'c25', cents: 25, label: 'Quarters', group: 'Coins' },
  { key: 'c10', cents: 10, label: 'Dimes', group: 'Coins' },
  { key: 'c5', cents: 5, label: 'Nickels', group: 'Coins' },
  { key: 'c1', cents: 1, label: 'Pennies', group: 'Coins' },
  { key: 'r25', cents: 1000, label: 'Quarters ($10 roll)', group: 'Rolls' },
  { key: 'r10', cents: 500, label: 'Dimes ($5 roll)', group: 'Rolls' },
  { key: 'r5', cents: 200, label: 'Nickels ($2 roll)', group: 'Rolls' },
  { key: 'r1', cents: 50, label: 'Pennies (50¢ roll)', group: 'Rolls' },
];

export function countTotal(count) {
  return DENOMINATIONS.reduce((n, d) => n + (Number(count[d.key]) || 0) * d.cents, 0);
}

/** The count to send: whole pieces only, zero rows dropped. */
export function cleanCount(count) {
  const out = {};
  for (const d of DENOMINATIONS) {
    const n = Number(count[d.key]) || 0;
    if (n > 0) out[d.key] = Math.floor(n);
  }
  return out;
}

export const MOVEMENT_LABELS = {
  sale_cash: 'Cash sale',
  refund_cash: 'Cash refund',
  paid_in: 'Paid in',
  paid_out: 'Paid out',
  drop: 'Drop to safe',
  no_sale: 'No sale',
  check_sale: 'Check',
};

// Forest buttons at the large control height: 44px on the counter, 52px in
// bench density (index.css .btn-* / .btn-lg). Counters run on tablets.
export const touchButton = {
  primary: 'btn-primary btn-lg',
  secondary: 'btn-secondary btn-lg',
  ghost: 'btn-ghost btn-lg',
};

// Money fields: a large control with tabular figures.
export const bigInputClass = 'input-field h-ctl-lg text-lg tabular';
// Text fields beside them (reason, notes, register name).
export const textInputClass = 'input-field h-ctl-lg';
// Labels wrapping a field.
export const fieldLabelClass = 'flex flex-col gap-1.5 text-[13px] font-medium text-gray-900';
export const errorTextClass = 'text-[13px] text-danger-fg';

// Register state tags: StatusTag's Forest geometry with semantic tones (a
// register isn't an order, so it has no StatusTag status of its own).
const TAG = 'inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold';
export const registerTag = {
  open: `${TAG} tag-dot bg-ok-bg text-ok-fg`,
  closed: `${TAG} bg-neutral-bg text-neutral-fg`,
  closing: `${TAG} tag-dot bg-prog-bg text-prog-fg`,
  review: `${TAG} tag-dot bg-warn-bg text-warn-fg`,
  reviewed: `${TAG} tag-check bg-ok-bg text-ok-fg`,
};

// Over/short ink: short (negative) in danger, over and even neutral.
export const overShortInk = (cents) => (Number(cents) < 0 ? 'text-danger-fg' : 'text-gray-900');
